// Case Buddy Next / Auto workflow assistant methods.
// These methods intentionally route through existing workflow/tool methods so
// spreadsheet-shaped case data remains the source of truth.

const CASE_AUTOMATION_DELAY_MIN_MS = 350;
const CASE_AUTOMATION_DELAY_BASE_MS = 1650;
const CASE_AUTOMATION_DELAY_MAX_MS = 4200;
const CASE_AUTOMATION_WAIT_POLL_MIN_MS = 450;
const CASE_AUTOMATION_WAIT_POLL_BASE_MS = 1100;
const CASE_AUTOMATION_WAIT_POLL_MAX_MS = 2400;
const CASE_AUTOMATION_OPERATION_POLL_MS = 180;
const CASE_AUTOMATION_SETTLE_AFTER_ACTION_MS = 260;
const CASE_AUTOMATION_MAX_OPERATION_WAIT_MS = 15000;
const CASE_AUTOMATION_WAYFINDER_HANDOFF_MAX_WAIT_MS = 6 * 60 * 60 * 1000;
const CASE_AUTOMATION_WAYFINDER_HANDOFF_POLL_MS = 6500;

function normalizeAutomationTag(tag = '') {
  return String(tag || '').trim().toUpperCase();
}

function firstPrimaryAction(step = {}) {
  const actions = Array.isArray(step.actions) ? step.actions : [];
  return actions.find(action => action.primary && !action.disabled) || actions.find(action => !action.disabled) || null;
}

function isPromiseLike(value) {
  return value && typeof value.then === 'function';
}

function delayCaseAutomation(ms = 0) {
  return new Promise(resolve => window.setTimeout(resolve, Math.max(0, Number(ms) || 0)));
}

async function resolveMaybePromise(value) {
  return isPromiseLike(value) ? await value : value;
}

export const caseBuddyAutomationMethods = {
  getCaseAutomationSteps() {
    return this.workflowGroupsView?.flatMap(group => group.steps || []) || [];
  },

  getCaseAutomationStepByTag(tag = '') {
    const normalizedTag = normalizeAutomationTag(tag);
    return this.getCaseAutomationSteps().find(step => normalizeAutomationTag(step.tag) === normalizedTag) || null;
  },

  getCaseAutomationSpeedDelayMs() {
    const speed = Math.max(0.25, Math.min(3, Number(this.caseAutomation?.speedMultiplier) || 1));
    const delay = Math.round(CASE_AUTOMATION_DELAY_BASE_MS / speed);
    return Math.max(CASE_AUTOMATION_DELAY_MIN_MS, Math.min(CASE_AUTOMATION_DELAY_MAX_MS, delay));
  },

  getCaseAutomationWaitPollDelayMs() {
    const speed = Math.max(0.25, Math.min(3, Number(this.caseAutomation?.speedMultiplier) || 1));
    const delay = Math.round(CASE_AUTOMATION_WAIT_POLL_BASE_MS / speed);
    return Math.max(CASE_AUTOMATION_WAIT_POLL_MIN_MS, Math.min(CASE_AUTOMATION_WAIT_POLL_MAX_MS, delay));
  },

  getCaseAutomationBlockingOperation() {
    if (this.caseChecksRunning) {
      return {
        waiting: true,
        reason: 'checks',
        status: 'Waiting for checks…',
        detail: 'Workflow checks are still running. Auto will continue when they finish.'
      };
    }
    if (this.caseGlobalOperation?.active) {
      return {
        waiting: true,
        reason: 'global-operation',
        status: 'Waiting for current operation…',
        detail: this.caseGlobalOperation.message || this.caseGlobalOperation.title || 'A case-level operation is still finishing.'
      };
    }
    if (this.caseModalOperation?.active) {
      return {
        waiting: true,
        reason: 'modal-operation',
        status: 'Waiting for modal work…',
        detail: this.caseModalOperation.message || this.caseModalOperation.title || 'A modal preview/review operation is still finishing.'
      };
    }
    return null;
  },

  getCaseAutomationOperationState() {
    const reasons = [];
    if (this.caseChecksRunning) reasons.push('workflow checks');
    if (this.caseGlobalOperation?.active) reasons.push(this.caseGlobalOperation.title || this.caseGlobalOperation.message || 'global operation');
    if (this.caseModalOperation?.active) reasons.push(this.caseModalOperation.title || this.caseModalOperation.message || 'modal operation');
    if (this.networkLookupRunning) reasons.push('network lookup');
    if (this.dateLookupRunning) reasons.push('date lookup');
    if (this.mainPathLabelAssignRunning) reasons.push('path-label assignment');
    if (this.tool68PreparationRunning) reasons.push('raw-data preparation');
    if (this.addressAttributionRunning) reasons.push('address attribution');
    return {
      active: reasons.length > 0,
      reasons,
      label: reasons.filter(Boolean).join(' · ')
    };
  },

  async waitForCaseAutomationWorkToSettle(options = {}) {
    const maxWaitMs = Math.max(750, Number(options.maxWaitMs) || Number(this.caseAutomation?.maxOperationWaitMs) || CASE_AUTOMATION_MAX_OPERATION_WAIT_MS);
    const pollMs = Math.max(80, Number(options.pollMs) || CASE_AUTOMATION_OPERATION_POLL_MS);
    const settleMs = Math.max(0, Number(options.settleMs) || CASE_AUTOMATION_SETTLE_AFTER_ACTION_MS);
    const startedAt = Date.now();
    let lastState = this.getCaseAutomationOperationState();

    while (lastState.active) {
      const waitedMs = Date.now() - startedAt;
      if (waitedMs >= maxWaitMs) {
        return {
          ready: false,
          waitedMs,
          reason: lastState.label || 'operation still active'
        };
      }
      if (!options.quiet) {
        this.setCaseAutomationStatus(
          'Waiting for Case Buddy to finish…',
          lastState.label ? `Still working: ${lastState.label}.` : 'A prior operation is still finishing.'
        );
      }
      await delayCaseAutomation(pollMs);
      if (options.fromAuto && (!this.caseAutomation.active || this.caseAutomation.paused)) {
        return { ready: false, waitedMs: Date.now() - startedAt, reason: 'automation stopped or paused' };
      }
      lastState = this.getCaseAutomationOperationState();
    }

    if (typeof this.yieldToCaseUi === 'function') {
      await this.yieldToCaseUi({ paint: true, frames: 2, minDelayMs: 24 });
    }
    if (settleMs > 0) {
      await delayCaseAutomation(settleMs);
      if (typeof this.yieldToCaseUi === 'function') {
        await this.yieldToCaseUi({ paint: true, frames: 1, minDelayMs: 8 });
      }
    }
    return { ready: true, waitedMs: Date.now() - startedAt, reason: '' };
  },

  resetCaseAutomationNoActionGuard() {
    this.caseAutomation.noActionStreak = 0;
    this.caseAutomation.noActionStartedAtMs = 0;
    this.caseAutomation.lastNoActionKey = '';
  },

  addCaseAutomationLog(message = '', details = {}) {
    const text = String(message || '').trim();
    if (!text) return;
    const current = Array.isArray(this.caseAutomation?.log) ? this.caseAutomation.log : [];
    this.caseAutomation.log = [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        atUtc: new Date().toISOString(),
        message: text,
        details
      },
      ...current
    ].slice(0, 24);
  },

  setCaseAutomationStatus(status = '', detail = '') {
    this.caseAutomation.status = String(status || '').trim() || 'Idle';
    this.caseAutomation.detail = String(detail || '').trim();
  },

  clearCaseAutomationTimer() {
    if (this.caseAutomationTimer) {
      window.clearTimeout(this.caseAutomationTimer);
      this.caseAutomationTimer = null;
    }
  },

  stopCaseAutomation(reason = 'Automation stopped.') {
    const wasActive = !!this.caseAutomation?.active;
    const completed = /^automation complete/i.test(String(reason || ''));
    this.clearCaseAutomationTimer();
    this.caseAutomation.active = false;
    this.caseAutomation.busy = false;
    this.caseAutomation.paused = false;
    this.caseAutomationShowcaseOverlayMinimized = false;
    this.caseAutomation.lastRunId = '';
    this.caseAutomation.waitingStreak = 0;
    this.caseAutomation.lastWaitingReason = '';
    this.caseAutomation.lastWaitingLogAtMs = 0;
    this.resetCaseAutomationNoActionGuard?.();
    this.caseAutomation.lastRepeatedStepKey = '';
    this.caseAutomation.repeatedStepCount = 0;
    if (this.caseAutomationTool88Activity?.active) {
      this.finishCaseAutomationTool88Activity?.({ status: 'automation-stopped', title: 'Tool 88 stopped', message: 'Automation was stopped while Tool 88 raw-data solving was active.' });
    }
    this.setCaseAutomationStatus(reason, 'Use Next to continue one action at a time, or Auto to resume automation.');
    this.addCaseAutomationLog(reason);
    if (wasActive && completed && this.caseSimpleLaunchAutoRequested) {
      this.caseSimpleLaunchLastCompletedAtUtc = new Date().toISOString();
      this.caseSimpleLaunchStatus = 'Automation completed. Opening the final report package preview.';
      this.caseSimpleLaunchDismissed = true;
      this.caseSimpleLaunchMinimized = false;
      window.setTimeout(() => {
        this.openCaseFullReportPackageModal?.({ automated: true, source: 'case-simple-launch-auto-complete' });
      }, 350);
    }
  },

  startCaseAutomation() {
    if (this.caseAutomation.active) {
      this.stopCaseAutomation('Automation stopped by user.');
      return;
    }
    this.caseAutomation.active = true;
    this.caseAutomation.paused = false;
    this.caseAutomationShowcaseOverlayMinimized = false;
    this.resetCaseAutomationNoActionGuard?.();
    this.caseAutomation.waitingStreak = 0;
    this.caseAutomation.lastWaitingReason = '';
    this.caseAutomation.lastWaitingLogAtMs = 0;
    this.caseAutomation.lastRepeatedStepKey = '';
    this.caseAutomation.repeatedStepCount = 0;
    this.resetCaseAutomationTool88Activity?.();
    this.caseAutomation.lastRunId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    this.setCaseAutomationStatus('Auto is running…', 'Case Buddy will run the same action that Next recommends until a manual pause or completion point is reached.');
    this.addCaseAutomationLog('Auto started.');
    this.scheduleCaseAutomationLoop(80);
  },

  scheduleCaseAutomationLoop(delayMs = null) {
    this.clearCaseAutomationTimer();
    if (!this.caseAutomation.active || this.caseAutomation.paused) return;
    const delay = Number.isFinite(Number(delayMs)) ? Number(delayMs) : this.getCaseAutomationSpeedDelayMs();
    this.caseAutomationTimer = window.setTimeout(() => {
      this.caseAutomationTimer = null;
      this.runCaseAutomationLoop();
    }, Math.max(0, delay));
  },

  async runCaseAutomationLoop() {
    if (!this.caseAutomation.active || this.caseAutomation.paused || this.caseAutomation.busy) return;
    const result = await this.runCaseAutomationNext({ fromAuto: true });
    if (!this.caseAutomation.active || this.caseAutomation.paused) return;
    if (result?.complete) {
      this.stopCaseAutomation('Automation complete.');
      return;
    }
    if (result?.manual) {
      this.caseAutomation.paused = true;
      return;
    }
    if (result?.acted) {
      this.resetCaseAutomationNoActionGuard?.();
    }
    if (result?.waiting || result?.busy || result?.settling) {
      this.caseAutomation.waitingStreak = (Number(this.caseAutomation.waitingStreak) || 0) + 1;
      this.caseAutomation.lastWaitingReason = result.reason || (result.busy ? 'busy' : 'waiting');
      const nowMs = Date.now();
      if (!this.caseAutomation.lastWaitingLogAtMs || nowMs - this.caseAutomation.lastWaitingLogAtMs > 8000) {
        this.addCaseAutomationLog(result.detail || result.status || 'Auto is waiting for Case Buddy to finish current work.', { reason: this.caseAutomation.lastWaitingReason });
        this.caseAutomation.lastWaitingLogAtMs = nowMs;
      }
      this.scheduleCaseAutomationLoop(result.waitDelayMs || this.getCaseAutomationWaitPollDelayMs());
      return;
    }
    this.caseAutomation.waitingStreak = 0;
    this.caseAutomation.lastWaitingReason = '';
    if (!result?.acted) {
      const nowMs = Date.now();
      const noActionKey = [
        normalizeAutomationTag(result?.plan?.stepTag),
        result?.plan?.method || result?.plan?.action || result?.plan?.type || 'no-plan'
      ].filter(Boolean).join('|');
      if (this.caseAutomation.lastNoActionKey !== noActionKey) {
        this.caseAutomation.lastNoActionKey = noActionKey;
        this.caseAutomation.noActionStreak = 0;
        this.caseAutomation.noActionStartedAtMs = nowMs;
      }
      this.caseAutomation.noActionStreak = (Number(this.caseAutomation.noActionStreak) || 0) + 1;
      if (!this.caseAutomation.noActionStartedAtMs) this.caseAutomation.noActionStartedAtMs = nowMs;
      const elapsedMs = nowMs - (Number(this.caseAutomation.noActionStartedAtMs) || nowMs);
      const maxStreak = Number(this.caseAutomation.maxNoActionStreak) || 80;
      const minElapsedMs = Number(this.caseAutomation.maxNoActionElapsedMs) || 90000;
      if (this.caseAutomation.noActionStreak >= maxStreak && elapsedMs >= minElapsedMs) {
        this.stopCaseAutomation('Auto stopped after repeated no-action cycles.');
        return;
      }
    }
    this.scheduleCaseAutomationLoop();
  },

  async runCaseAutomationNext(options = {}) {
    if (this.caseAutomation.busy) {
      if (!options.fromAuto) this.showCaseToast('Next/Auto is already working on an action.');
      return { acted: false, busy: true, waiting: true, reason: 'automation-busy', waitDelayMs: this.getCaseAutomationWaitPollDelayMs() };
    }
    const blockingOperation = this.getCaseAutomationBlockingOperation?.();
    if (blockingOperation?.waiting) {
      if (options.fromAuto) {
        this.setCaseAutomationStatus(blockingOperation.status, blockingOperation.detail);
        return { acted: false, waiting: true, reason: blockingOperation.reason, status: blockingOperation.status, detail: blockingOperation.detail, waitDelayMs: this.getCaseAutomationWaitPollDelayMs() };
      }
      this.setCaseAutomationStatus(blockingOperation.status, blockingOperation.detail);
      this.showCaseToast('Case Buddy is already working. Try Next again when the current operation finishes.');
      return { acted: false, waiting: true, reason: blockingOperation.reason, waitDelayMs: this.getCaseAutomationWaitPollDelayMs() };
    }

    this.caseAutomation.busy = true;
    this.caseAutomation.paused = false;
    try {
      await this.yieldToCaseUi?.();
      const plan = this.getCaseAutomationNextPlan();
      this.caseAutomation.lastPlan = plan;
      if (!plan) {
        this.setCaseAutomationStatus('No next action found.', 'Run checks or review the workflow manually.');
        return { acted: false };
      }
      if (plan.type === 'complete') {
        this.setCaseAutomationStatus('Workflow complete.', 'All workflow steps appear complete for the current known check/signal state.');
        this.addCaseAutomationLog('Workflow complete.');
        return { acted: false, complete: true };
      }
      if (plan.type === 'manual-pause') {
        this.openCaseAutomationManualPause(plan);
        return { acted: false, manual: true };
      }
      this.setCaseAutomationStatus(plan.status || 'Running next action…', plan.detail || plan.stepTitle || '');
      this.addCaseAutomationLog(plan.log || plan.status || 'Running next action.', { stepTag: plan.stepTag || '', action: plan.action || '' });
      const acted = await this.executeCaseAutomationPlan(plan);
      const settleResult = await this.waitForCaseAutomationWorkToSettle?.({
        fromAuto: !!options.fromAuto,
        maxWaitMs: Number(this.caseAutomation?.maxOperationWaitMs) || CASE_AUTOMATION_MAX_OPERATION_WAIT_MS,
        quiet: false
      });
      if (settleResult && !settleResult.ready) {
        return {
          acted: !!acted,
          waiting: true,
          settling: true,
          reason: settleResult.reason || 'operation-settle-timeout',
          status: 'Waiting for Case Buddy to finish…',
          detail: settleResult.reason || 'A previous action is still finishing.',
          waitDelayMs: this.getCaseAutomationWaitPollDelayMs(),
          plan
        };
      }
      await this.yieldToCaseUi?.({ paint: true, frames: 2, minDelayMs: 24 });
      if (this.caseAutomation.paused) {
        return { acted: !!acted, manual: true, plan };
      }
      const repeatGuard = this.updateCaseAutomationRepeatGuard(plan, !!acted);
      if (options.fromAuto && repeatGuard.shouldPause) {
        this.openCaseAutomationManualPause({
          type: 'manual-pause',
          pauseKind: 'automation-repeat-guard',
          stepTag: plan.stepTag || '',
          stepTitle: plan.stepTitle || '',
          title: 'Automation paused after repeated attempts',
          message: `Auto tried “${plan.stepTitle || plan.stepTag || 'the current step'}” ${repeatGuard.count} times, but the workflow step still did not become complete. Review this step manually, then choose Done / continue or Stop Auto.`,
          primaryLabel: this.getCaseAutomationPrimaryHelperLabel(plan.stepTag),
          primaryMethod: this.getCaseAutomationPrimaryHelperMethod(plan.stepTag)
        });
        return { acted: false, manual: true, plan, repeatGuard };
      }
      if (!options.fromAuto && this.caseAutomation.active && !this.caseAutomation.paused) {
        this.scheduleCaseAutomationLoop(this.getCaseAutomationSpeedDelayMs());
      }
      return { acted: !!acted, plan };
    } catch (error) {
      const message = error?.message || 'Automation action failed.';
      this.setCaseAutomationStatus('Automation paused after an error.', message);
      this.addCaseAutomationLog(`Error: ${message}`);
      if (this.caseAutomation.active) this.caseAutomation.paused = true;
      this.showCaseToast(message);
      return { acted: false, error: message };
    } finally {
      this.caseAutomation.busy = false;
    }
  },

  getCaseAutomationPlanRepeatKey(plan = {}) {
    return [
      normalizeAutomationTag(plan.stepTag),
      plan.type || '',
      plan.method || plan.signalCode || plan.workflowAction?.id || plan.action || ''
    ].filter(Boolean).join('|');
  },

  updateCaseAutomationRepeatGuard(plan = {}, acted = false) {
    if (this.getCaseAutomationBlockingOperation?.()?.waiting) {
      return { stillIncomplete: false, count: Number(this.caseAutomation.repeatedStepCount) || 0, shouldPause: false, waiting: true };
    }
    const key = this.getCaseAutomationPlanRepeatKey(plan);
    if (!key || !plan.stepTag) {
      this.caseAutomation.lastRepeatedStepKey = '';
      this.caseAutomation.repeatedStepCount = 0;
      return { stillIncomplete: false, count: 0, shouldPause: false };
    }
    const step = this.getCaseAutomationStepByTag(plan.stepTag);
    const stillIncomplete = !!step && !step.complete;
    if (!stillIncomplete) {
      this.caseAutomation.lastRepeatedStepKey = '';
      this.caseAutomation.repeatedStepCount = 0;
      return { stillIncomplete: false, count: 0, shouldPause: false };
    }
    const nextCount = this.caseAutomation.lastRepeatedStepKey === key
      ? (Number(this.caseAutomation.repeatedStepCount) || 0) + 1
      : 1;
    this.caseAutomation.lastRepeatedStepKey = key;
    this.caseAutomation.repeatedStepCount = nextCount;
    const maxAttempts = Math.max(2, Number(this.caseAutomation.maxRepeatedStepAttempts) || 3);
    return {
      stillIncomplete,
      acted: !!acted,
      key,
      count: nextCount,
      maxAttempts,
      shouldPause: nextCount >= maxAttempts
    };
  },

  getCaseAutomationPrimaryHelperMethod(stepTag = '') {
    const tag = normalizeAutomationTag(stepTag);
    const helpers = {
      ADDENDUM_BUILD: 'openCaseAddendumModal',
      CASE_FLOW_VISUALS: 'openCaseFlowVisualsModal',
      CASE_COMPLETION_SUMMARY: 'openCaseFullReportPackageModal',
      TEMPLATE_DELIVERABLE: 'openTemplateDeliverableModal',
      SUSPICIOUS_ADDRESS_TABLE: 'openSuspiciousAddressTableModal',
      EXCHANGE_ADDRESS_TABLE: 'openExchangeAddressTableModal',
      DORMANT_ADDRESS_DELIVERABLES: 'openDormantAddressDeliverablesModal',
      WHAT_WENT_WHERE: 'openWhatWentWhereModal',
      CASE_CONCLUSION: 'openCaseConclusionModal',
      REPORT_BODY: 'openReportBodyModal'
    };
    return helpers[tag] || '';
  },

  getCaseAutomationPrimaryHelperLabel(stepTag = '') {
    const tag = normalizeAutomationTag(stepTag);
    const labels = {
      ADDENDUM_BUILD: 'Open addendum builder',
      CASE_FLOW_VISUALS: 'Open visual builder',
      CASE_COMPLETION_SUMMARY: 'Open final package',
      TEMPLATE_DELIVERABLE: 'Open template builder',
      SUSPICIOUS_ADDRESS_TABLE: 'Open suspicious table builder',
      EXCHANGE_ADDRESS_TABLE: 'Open exchange table builder',
      DORMANT_ADDRESS_DELIVERABLES: 'Open dormant deliverables',
      WHAT_WENT_WHERE: 'Open What Went Where',
      CASE_CONCLUSION: 'Open conclusion builder',
      REPORT_BODY: 'Open report body builder'
    };
    return labels[tag] || '';
  },

  getCaseAutomationNextPlan() {
    const steps = this.getCaseAutomationSteps();
    const nextStep = steps.find(step => !step.complete) || null;
    if (!nextStep) return { type: 'complete' };

    const tag = normalizeAutomationTag(nextStep.tag);
    const primaryAction = firstPrimaryAction(nextStep);
    const base = {
      stepTag: tag,
      stepTitle: nextStep.title,
      action: primaryAction?.id || '',
      status: `Next: ${nextStep.title}`,
      detail: nextStep.description || ''
    };

    if (tag === 'CASE_INTAKE') {
      const confirmationState = typeof this.getStartingMainTransactionConfirmationState === 'function'
        ? this.getStartingMainTransactionConfirmationState()
        : null;
      if (!confirmationState || confirmationState.count <= 0) {
        return {
          ...base,
          type: 'manual-pause',
          pauseKind: 'starting-main-transactions',
          title: 'Load starting main transactions',
          message: 'Paste or enter the starting transaction rows in Main Data. Automation cannot safely invent the starting case rows. If you pasted them manually, choose Done / continue or use Check / Confirm on the workflow step.',
          primaryLabel: 'Open starting transactions helper',
          primaryMethod: 'openStartingMainTransactionsModal'
        };
      }
      return { ...base, type: 'method', method: 'automationConfirmStartingMainTransactions', status: 'Confirming starting transactions…' };
    }

    if (tag === 'RAW_DATA_FETCH') {
      const missingCount = Number(this.caseSummary?.rawMissingMainTransactions) || 0;
      if (missingCount > 0 || (Number(this.caseSummary?.rawEntries) || 0) <= 0) {
        const canUseInternalHopSolver = this.isCaseAutomationInternalHopSolverRawDataEnabled?.();
        const canUseWayFinder = this.isCaseAutomationWayFinderRawHandoffEnabled?.();
        if (canUseInternalHopSolver || canUseWayFinder) {
          const usingInternalFirst = !!canUseInternalHopSolver;
          return {
            ...base,
            type: 'method',
            method: 'automationFetchRawDataViaConfiguredSource',
            status: usingInternalFirst ? 'Solving raw data with Tool 88…' : 'Fetching raw data through WayFinder…',
            detail: missingCount > 0
              ? `${missingCount} target${missingCount === 1 ? '' : 's'} need raw-data coverage. Auto will ${usingInternalFirst ? 'try embedded Tool 88 first, then fall back to WayFinder if needed.' : 'queue them through the local WayFinder bridge and import completed TSV results into Raw Data.'}`
              : usingInternalFirst
                ? 'Auto will try embedded Tool 88 before moving on to WayFinder fallback.'
                : 'Auto will check the local WayFinder bridge for raw-data coverage before moving on.'
          };
        }
        return {
          ...base,
          type: 'manual-pause',
          pauseKind: 'raw-data-fetch',
          title: 'Load raw data for main transactions',
          message: missingCount > 0
            ? `${missingCount} starting/main transaction${missingCount === 1 ? '' : 's'} still need raw coverage. Use embedded Tool 88, WayFinder handoff, or paste each fetched raw flow into the manual raw-data helper, then choose Done / Continue.`
            : 'Raw data is still needed before the sapling can be prepared. Use embedded Tool 88, WayFinder handoff, or paste fetched raw rows into the manual raw-data helper, then choose Done / Continue.',
          primaryLabel: 'Open Solve Hop',
          primaryMethod: 'openCaseHopSolverNextUnresolved'
        };
      }
      return { ...base, type: 'run-checks', status: 'Checking raw-data coverage…' };
    }

    const planByTag = {
      NETWORK_DISCOVERY: { type: 'method', method: 'automationIdentifyTransactionNetworks', status: 'Looking up transaction networks…' },
      TRANSACTION_DATES: { type: 'method', method: 'automationCompleteDatesAndSort', status: 'Completing UTC dates and sorting entries…' },
      MAIN_TRANSACTION_PATH_LABELS: { type: 'method', method: 'automationAssignMainPathLabels', status: 'Assigning main transaction path labels…' },
      CASE_CONTEXT_REVIEW: { type: 'signal', signalCode: 'CASE_CONTEXT_REVIEWED', source: 'case-automation-next', status: 'Recording case-context review…' },
      RAW_TO_MAIN_PREPARATION: { type: 'method', method: 'automationPrepareRawIntoMain', status: 'Preparing raw data into Main Data…' },
      ADDRESS_ATTRIBUTION_FILL: this.caseAutomation.skipResolvableReview
        ? { type: 'method', method: 'automationFillMissingAttributionAsUnknown', status: 'Filling missing attribution as Unknown…' }
        : {
            type: 'manual-pause',
            pauseKind: 'address-attribution',
            title: 'Fill address attribution in main data',
            message: 'Review the missing addresses and write known attribution or Unknown into Column K. Enable the automation skip toggle to let Case Buddy fill outstanding attribution as Unknown.',
            primaryLabel: 'Open attribution helper',
            primaryMethod: 'openAddressAttributionModal'
          },
      ATTRIBUTION_PATH_OPERATIONS: this.caseAutomation.skipResolvableReview
        ? { type: 'method', method: 'automationMarkPathOperationsReviewed', status: 'Marking path-operation review complete…' }
        : {
            type: 'manual-pause',
            pauseKind: 'path-operations',
            title: 'Review attributed rows and resolve path operations',
            message: 'Review prune, graft, and Bridge graft needs. Automation pauses here unless the skip toggle is enabled.',
            primaryLabel: 'Open prune / graft / Bridge review',
            primaryMethod: 'openPathOperationModal'
          },
      ADDRESS_COLOR_LIKE_CELLS: { type: 'method', method: 'automationApplyAddressColors', status: 'Coloring matching From/To addresses…' },
      ENTITY_REVIEW: this.caseAutomation.skipResolvableReview
        ? { type: 'method', method: 'automationRubberStampEntityReview', status: 'Rubber-stamping entity review…' }
        : {
            type: 'manual-pause',
            pauseKind: 'entity-review',
            title: 'Review new entities and local curators',
            message: 'Review candidate entity terms and local curator updates. Enable the skip toggle to mark the current candidate set reviewed without adding new local terms.',
            primaryLabel: 'Open entity review',
            primaryMethod: 'openEntityCuratorReviewModal'
          },
      EXTERNAL_ATTRIBUTION_REVIEW: { type: 'signal', signalCode: 'EXTERNAL_ATTRIBUTION_REVIEWED', source: 'case-automation-next', status: 'Recording external attribution review…' },
      CASE_INCONSISTENCIES: { type: 'method', method: 'automationResolveCaseInconsistencies', status: 'Applying available safe inconsistency fixes…' },
      NORMALIZE_DEPOSIT_NOTES: { type: 'method', method: 'automationNormalizeDepositHotWalletNotes', status: 'Normalizing deposit, hot-wallet, and unknown-service notes…' },
      HIGH_PRIORITY_DEPOSIT_PATTERNS: { type: 'method', method: 'automationCompleteDepositPatternReview', status: 'Completing deposit-pattern review…' },
      KNOWN_ENTITY_NOTE_CASING: { type: 'method', method: 'automationReviewKnownEntityCasing', status: 'Reviewing known-entity note casing…' },
      DUPLICATE_HASH_LABELS: { type: 'method', method: 'automationLabelDuplicateHashes', status: 'Labeling duplicate transaction hashes…' },
      NOTES_BACKUP_AND_HASH_REPLACEMENT: { type: 'method', method: 'automationBackupAndRewriteNotes', status: 'Backing up notes and replacing hashes with path labels…' },
      WWW_USD_REFERENCES: { type: 'method', method: 'automationReviewWwwUsdReferences', status: 'Reviewing top-level USD reference values…' },
      ADDRESS_LEGEND: { type: 'method', method: 'automationBuildAndReviewAddressLegend', status: 'Building and reviewing address legend…' },
      TEMPLATE_DELIVERABLE: { type: 'method', method: 'automationBuildAndReviewTemplate', status: 'Building and reviewing template deliverable…' },
      SUSPICIOUS_ADDRESS_TABLE: { type: 'method', method: 'automationBuildAndReviewSuspiciousTable', status: 'Building and reviewing suspicious address table…' },
      EXCHANGE_ADDRESS_TABLE: { type: 'method', method: 'automationBuildAndReviewExchangeTable', status: 'Building and reviewing exchange address table…' },
      DORMANT_ADDRESS_DELIVERABLES: { type: 'method', method: 'automationBuildAndReviewDormantDeliverables', status: 'Building and reviewing dormant address deliverables…' },
      WHAT_WENT_WHERE: { type: 'method', method: 'automationBuildAndReviewWhatWentWhere', status: 'Building and reviewing What Went Where…' },
      CASE_CONCLUSION: { type: 'method', method: 'automationBuildAndReviewConclusion', status: 'Building and reviewing case conclusion…' },
      REPORT_BODY: { type: 'method', method: 'automationBuildAndReviewReportBody', status: 'Building and reviewing report body…' },
      PATH_ENDING_ADDENDUM_REVIEW: { type: 'method', method: 'automationReviewAddendumPathEndings', status: 'Reviewing addendum path-ending rows…' },
      ADDENDUM_PATH_ENDINGS: { type: 'method', method: 'automationReviewAddendumPathEndings', status: 'Reviewing addendum path-ending rows…' },
      ADDENDUM_RECIPIENT_ENTITY: this.caseAutomation.skipResolvableReview
        ? { type: 'method', method: 'automationResolveAddendumRecipientsWithFirstChoice', status: 'Resolving addendum recipient entities…' }
        : { type: 'method', method: 'automationResolveAddendumRecipientsOrPause', status: 'Resolving addendum recipient entities…' },
      ADDENDUM_BUILD: { type: 'method', method: 'automationBuildAndReviewAddendum', status: 'Building and reviewing addendum…' },
      CASE_FLOW_VISUALS: { type: 'method', method: 'automationReviewCaseFlowVisuals', status: 'Reviewing case flow visuals…' },
      CASE_COMPLETION_SUMMARY: { type: 'method', method: 'automationReviewFullReportPackage', status: 'Reviewing final one-click report package…' }
    };

    const mapped = planByTag[tag];
    if (mapped) return { ...base, ...mapped };
    if (primaryAction) return { ...base, type: 'workflow-action', workflowAction: primaryAction, status: `Opening ${primaryAction.label || nextStep.title}…` };
    return { ...base, type: 'run-checks', status: 'Refreshing workflow checks…' };
  },

  async executeCaseAutomationPlan(plan = {}) {
    if (plan.type === 'run-checks') {
      await this.runWorkflowChecks();
      return true;
    }
    if (plan.type === 'signal') {
      this.recordWorkflowSignal(plan.signalCode, plan.source || 'case-automation-next', { silent: true });
      this.showCaseToast(`${plan.stepTitle || 'Workflow step'} marked reviewed.`);
      return true;
    }
    if (plan.type === 'workflow-action') {
      this.runWorkflowAction(plan.workflowAction, this.getCaseAutomationStepByTag(plan.stepTag));
      return true;
    }
    if (plan.type === 'method') {
      const fn = this[plan.method];
      if (typeof fn !== 'function') throw new Error(`Automation method not found: ${plan.method}`);
      const result = fn.call(this, plan);
      return isPromiseLike(result) ? await result : result !== false;
    }
    return false;
  },

  openCaseAutomationManualPause(plan = {}) {
    this.caseAutomation.paused = !!this.caseAutomation.active;
    this.caseAutomation.manualPause = {
      ...plan,
      openedAtUtc: new Date().toISOString()
    };
    this.showCaseAutomationPauseModal = true;
    this.setCaseAutomationStatus(plan.title || 'Manual action required', plan.message || 'Complete this step manually, then continue.');
    this.addCaseAutomationLog(`Manual pause: ${plan.title || plan.stepTitle || 'Workflow step'}`);
  },

  closeCaseAutomationPauseModal() {
    this.showCaseAutomationPauseModal = false;
  },

  async runCaseAutomationPausePrimaryAction() {
    const pause = this.caseAutomation.manualPause || {};
    const fn = pause.primaryMethod ? this[pause.primaryMethod] : null;
    if (typeof fn !== 'function') {
      this.showCaseToast('No helper action is available for this manual pause.');
      return false;
    }
    this.closeCaseAutomationPauseModal();
    const result = fn.call(this);
    return isPromiseLike(result) ? await result : result;
  },

  async caseAutomationManualPauseDone() {
    this.closeCaseAutomationPauseModal();
    this.caseAutomation.paused = false;
    this.addCaseAutomationLog('Manual pause marked done.');
    this.setCaseAutomationStatus('Manual step marked done.', this.caseAutomation.active ? 'Auto will continue after a short pause.' : 'Use Next to continue.');
    if (this.caseAutomation.manualPause?.pauseKind === 'starting-main-transactions' && typeof this.confirmStartingMainTransactionsLoaded === 'function') {
      this.confirmStartingMainTransactionsLoaded({ silent: true });
    } else {
      await this.runWorkflowChecks();
    }
    if (this.caseAutomation.active) this.scheduleCaseAutomationLoop(this.getCaseAutomationSpeedDelayMs());
  },

  minimizeCaseAutomationShowcaseOverlay() {
    this.caseAutomationShowcaseOverlayMinimized = true;
  },

  restoreCaseAutomationShowcaseOverlay() {
    this.caseAutomationShowcaseOverlayMinimized = false;
  },

  isCaseAutomationWayFinderRawHandoffEnabled() {
    return !!(this.caseAutomationWayFinderHandoffEnabled && this.caseAutomationRawDataSourceMode === 'wayfinder');
  },

  caseSimpleLaunchStepClass(step = '') {
    const key = String(step || '').toLowerCase();
    const startingCount = Number(this.getStartingMainTransactionConfirmationState?.()?.count) || 0;
    const complete = !!(this.workflowProgress?.total && Number(this.workflowProgress.completed) >= Number(this.workflowProgress.total));
    return {
      'case-simple-launch__step': true,
      'case-simple-launch__step--done':
        key === 'case' ||
        (key === 'transactions' && startingCount > 0) ||
        (key === 'report' && complete),
      'case-simple-launch__step--active':
        (key === 'transactions' && startingCount <= 0) ||
        (key === 'auto' && startingCount > 0 && !complete),
      'case-simple-launch__step--pending':
        key === 'report' && !complete
    };
  },

  dismissCaseSimpleLaunch() {
    this.caseSimpleLaunchDismissed = true;
    this.caseSimpleLaunchMinimized = false;
    this.caseSimpleFileChooserVisible = false;
  },

  minimizeCaseSimpleLaunch() {
    this.caseSimpleLaunchMinimized = true;
    this.caseSimpleFileChooserVisible = false;
  },

  restoreCaseSimpleLaunch() {
    this.caseSimpleLaunchDismissed = false;
    this.caseSimpleLaunchMinimized = false;
  },

  toggleCaseSimpleFileChooser() {
    this.caseSimpleFileChooserVisible = !this.caseSimpleFileChooserVisible;
  },

  closeCaseSimpleFileChooser() {
    this.caseSimpleFileChooserVisible = false;
  },

  formatProjectFileDate(value = '') {
    if (!value) return '';
    try {
      return new Date(value).toLocaleString();
    } catch {
      return String(value || '');
    }
  },

  loadCaseSimpleLaunchProjectFile(fileId = '') {
    const targetId = String(fileId || '');
    if (!targetId || targetId === this.currentProjectFileId) {
      this.caseSimpleFileChooserVisible = false;
      return false;
    }
    if (typeof this.maybeConfirmProjectSwitch === 'function' && !this.maybeConfirmProjectSwitch(targetId)) {
      return false;
    }
    const targetName = this.projectFiles?.find?.(file => file.id === targetId)?.name || 'selected case';
    this.switchToProjectFile?.(targetId);
    this.caseSimpleFileChooserVisible = false;
    this.caseSimpleLaunchStatus = `Loaded ${targetName}. Load starting transactions or run Auto when ready.`;
    this.restoreCaseSimpleLaunch?.();
    return true;
  },

  async openCaseSimpleLaunchFinalReport() {
    if (!this.caseSimpleLaunchReportReady) {
      this.caseSimpleLaunchStatus = 'The final report package will be available after the workflow is complete.';
      return false;
    }
    this.caseSimpleLaunchMinimized = true;
    this.caseSimpleFileChooserVisible = false;
    this.caseSimpleLaunchStatus = 'Opening the final report package preview.';
    if (typeof this.openCaseFullReportPackageModal === 'function') {
      await this.openCaseFullReportPackageModal({ view: 'rich' });
      return true;
    }
    this.showCaseToast?.('Final report package preview is not available yet.');
    return false;
  },

  openCaseSimpleDetailsPrompt() {
    this.caseSimpleDetailsDraft = {
      caseName: this.caseDetails?.caseName || '',
      caseId: this.caseDetails?.caseId || '',
      caseType: this.caseDetails?.caseType || ''
    };
    this.caseSimpleDetailsVisible = true;
  },

  closeCaseSimpleDetailsPrompt(skipped = false) {
    this.caseSimpleDetailsVisible = false;
    if (skipped) {
      this.caseSimpleLaunchStatus = 'Case details skipped for now. You can edit them later from the full workspace.';
    }
    this.restoreCaseSimpleLaunch?.();
  },

  saveCaseSimpleDetailsPrompt() {
    this.caseSupportState.caseDetails = {
      caseName: String(this.caseSimpleDetailsDraft?.caseName || '').trim().slice(0, 120),
      caseId: String(this.caseSimpleDetailsDraft?.caseId || '').trim().slice(0, 80),
      caseType: String(this.caseSimpleDetailsDraft?.caseType || '').trim().slice(0, 80)
    };
    this.caseSimpleDetailsVisible = false;
    this.markCaseDataMutated?.('case_details_updated', { metadataOnly: true });
    if (this.autoSaveEnabled) {
      this.saveCurrentProjectFile?.({ persistInput: true, persistOutput: false, markSavedAt: true });
    }
    this.caseSimpleLaunchStatus = 'Case details saved. Load starting transactions when ready.';
    this.restoreCaseSimpleLaunch?.();
    this.showCaseToast?.('Case details saved.');
    return true;
  },

  createCaseSimpleLaunchCase() {
    this.caseSimpleFileChooserVisible = false;
    if (this.autoSaveEnabled) {
      this.saveCurrentProjectFile?.();
    } else if (this.caseSupportState?.dirty) {
      const confirmed = window.confirm('Create a new case without saving the current case first?');
      if (!confirmed) return false;
    }
    const suggestedName = this.nextDefaultProjectFileName?.() || `Case Buddy ${new Date().toLocaleDateString()}`;
    const nextFile = this.createProjectFileMeta?.(suggestedName);
    if (!nextFile) {
      this.showCaseToast?.('Could not create a new case file.');
      return false;
    }
    const currentIndex = this.projectFiles.findIndex(file => file.id === this.currentProjectFileId);
    const insertIndex = currentIndex >= 0 ? currentIndex + 1 : this.projectFiles.length;
    this.projectFiles.splice(insertIndex, 0, nextFile);
    this.createDefaultCaseStorageForFile?.(nextFile.id);
    this.persistProjectFilesIndex?.(this.currentProjectFileId);
    this.switchToProjectFile?.(nextFile.id, { bootstrapBlank: false });
    this.$nextTick?.(() => this.ensureCaseWorkbookScaffold?.({ silent: true }));
    this.caseSimpleLaunchStatus = `Created ${nextFile.name}. Add basic details or skip them, then load starting transactions.`;
    this.showCaseToast?.(`Created new Case Buddy file: ${nextFile.name}`);
    this.$nextTick?.(() => this.openCaseSimpleDetailsPrompt?.());
    return true;
  },

  openCaseSimpleLaunchStartingTransactions() {
    this.caseSimpleFileChooserVisible = false;
    this.caseSimpleLaunchReturnAfterStartingModal = true;
    this.caseSimpleLaunchMinimized = true;
    this.openStartingMainTransactionsModal?.();
    this.caseSimpleLaunchStatus = 'Load or paste the starting transactions. Case Buddy will return to the simple view after this step.';
  },

  startCaseSimpleLaunchAuto() {
    this.caseSimpleFileChooserVisible = false;
    this.caseAutomation.skipResolvableReview = !!this.caseSimpleLaunchAutoSkipResolvable;
    this.caseSimpleLaunchAutoRequested = true;
    this.caseSimpleLaunchDismissed = true;
    this.caseSimpleLaunchMinimized = false;
    this.caseSimpleLaunchStatus = 'Auto is running. Case Buddy will use WayFinder for raw data if the local bridge is available.';
    if (!this.caseAutomation.active) {
      this.startCaseAutomation();
    }
  },

  automationConfirmStartingMainTransactions() {
    const result = this.confirmStartingMainTransactionsLoaded?.({ silent: true });
    const state = this.getStartingMainTransactionConfirmationState?.();
    this.showCaseToast(state?.pass
      ? `Confirmed ${state.count} starting main transaction row${state.count === 1 ? '' : 's'} in Main Data.`
      : (state?.message || 'Starting transactions could not be confirmed.'));
    return result !== false;
  },

  async automationFetchRawDataViaConfiguredSource(plan = {}) {
    if (this.isCaseAutomationInternalHopSolverRawDataEnabled?.()) {
      const internalResult = await this.runCaseHopSolverRawFetchUntilComplete?.({
        fromAuto: !!this.caseAutomation.active,
        guardrails: {
          speedMultiplier: this.caseAutomation?.speedMultiplier || 1
        }
      });
      if (internalResult?.ok) {
        this.addCaseAutomationLog(`Tool 88 raw data ready: ${internalResult.rowsAppended || 0} row${(internalResult.rowsAppended || 0) === 1 ? '' : 's'} imported during Auto.`);
        return true;
      }
      if (internalResult?.userStopped) return false;
      if (this.caseAutomationRawDataSourceMode === 'internal-hop-solver' && !this.isCaseAutomationWayFinderRawHandoffEnabled?.()) {
        this.openCaseAutomationManualPause({
          type: 'manual-pause',
          pauseKind: 'raw-data-fetch-internal-hop-solver',
          stepTag: plan.stepTag || 'RAW_DATA_FETCH',
          stepTitle: plan.stepTitle || 'Load raw data for main transactions',
          title: 'Tool 88 raw-data solving needs review',
          message: internalResult?.message || 'Tool 88 did not return raw rows that could be imported automatically. Review the Solve Hop modal, run one step manually, or paste raw rows.',
          primaryLabel: 'Open Solve Hop',
          primaryMethod: 'openCaseHopSolverNextUnresolved'
        });
        return false;
      }
      this.addCaseAutomationLog(`Tool 88 raw-data attempt did not finish automatically; falling back to WayFinder. ${internalResult?.message || ''}`.trim());
    }

    if (!this.isCaseAutomationWayFinderRawHandoffEnabled?.()) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'raw-data-fetch',
        stepTag: plan.stepTag || 'RAW_DATA_FETCH',
        stepTitle: plan.stepTitle || 'Load raw data for main transactions',
        title: 'Load raw data for main transactions',
        message: 'Automatic raw-data loading is not available. Use embedded Tool 88, WayFinder handoff, or paste raw data manually, then choose Done / Continue.',
        primaryLabel: 'Open Solve Hop',
        primaryMethod: 'openCaseHopSolverNextUnresolved'
      });
      return false;
    }

    const result = await this.runWayFinderHandoffUntilRawCoverageComplete?.({
      fromAuto: !!this.caseAutomation.active,
      maxWaitMs: CASE_AUTOMATION_WAYFINDER_HANDOFF_MAX_WAIT_MS,
      pollMs: CASE_AUTOMATION_WAYFINDER_HANDOFF_POLL_MS,
      quietToast: true
    });

    if (result?.ok) {
      this.addCaseAutomationLog(`WayFinder raw data ready: ${result.rowsAppended || 0} row${(result.rowsAppended || 0) === 1 ? '' : 's'} imported during Auto.`);
      return true;
    }

    if (result?.userStopped) return false;

    this.openCaseAutomationManualPause({
      type: 'manual-pause',
      pauseKind: 'raw-data-fetch',
      stepTag: plan.stepTag || 'RAW_DATA_FETCH',
      stepTitle: plan.stepTitle || 'Load raw data for main transactions',
      title: 'Raw-data loading needs attention',
      message: result?.message || 'Case Buddy could not finish automatic raw-data loading. Use the Solve Hop modal, open the WayFinder handoff queue, confirm WayFinder is running in Case Buddy mode, or paste raw data manually.',
      primaryLabel: 'Open Solve Hop',
      primaryMethod: 'openCaseHopSolverNextUnresolved'
    });
    return false;
  },

  async automationIdentifyTransactionNetworks() {
    await this.openNetworkLookupModal({ automated: true });
    this.selectPendingNetworkLookupRows?.();
    await this.runNetworkDiscoveryLookup?.();
    if (this.networkLookupLastRunSummary?.unresolved === 0 && !this.networkLookupRunning) this.showNetworkLookupModal = false;
    return true;
  },

  async automationCompleteDatesAndSort() {
    await this.openDateLookupModal({ automated: true });
    this.selectPendingDateLookupRows?.();
    await this.runMissingDateLookup?.();
    await this.yieldToCaseUi?.();
    const sortResult = this.sortMainEntriesByDate?.({ silent: true });
    if (isPromiseLike(sortResult)) await sortResult;
    if (!this.dateLookupRunning) this.showDateLookupModal = false;
    return true;
  },

  async automationAssignMainPathLabels() {
    await this.openMainPathLabelModal({ automated: true });
    const result = this.applyMainPathLabels?.();
    if (isPromiseLike(result)) await result;
    if (!this.mainPathLabelAssignRunning) this.showMainPathLabelModal = false;
    return true;
  },

  async automationPrepareRawIntoMain() {
    await this.openTool68PreparationModal({ automated: true });
    this.selectUnprocessedTool68PreparationRows?.();
    if ((Number(this.tool68PreparationSelectedCount) || 0) <= 0 && (Number(this.tool68PreparationReprocessAvailableCount) || 0) > 0) {
      this.selectReprocessAvailableTool68PreparationRows?.();
      this.tool68PreparationConfirmReprocess = true;
    }
    const result = this.runTool68PreparationForSelectedRows?.();
    if (isPromiseLike(result)) await result;
    if (!this.tool68PreparationRunning && this.tool68PreparationLastRunSummary?.complete) this.showTool68PreparationModal = false;
    return true;
  },

  async automationFillMissingAttributionAsUnknown() {
    await this.openAddressAttributionModal({ automated: true });
    this.fillOutstandingAddressAttributionsAsUnknown?.();
    const result = this.applyAddressAttributionRows?.();
    if (isPromiseLike(result)) await result;
    if (!this.addressAttributionRunning) this.showAddressAttributionModal = false;
    return true;
  },

  automationMarkPathOperationsReviewed() {
    this.refreshPathOperationCandidates?.({ silent: true });
    this.markAttributionPathOperationsCompleteFromModal?.();
    this.showPathOperationModal = false;
    return true;
  },

  automationApplyAddressColors() {
    const result = this.applyCaseAddressColors?.();
    return result !== false;
  },

  async automationRubberStampEntityReview() {
    await this.openEntityCuratorReviewModal({ automated: true });
    const caseFixResult = this.applyAllEntityCuratorCaseMismatchCasingFixes?.({
      skipConfirm: true,
      source: 'case-automation-next-entity-case-match'
    });
    if (isPromiseLike(caseFixResult)) await caseFixResult;
    await this.yieldToCaseUi?.({ paint: true, frames: 2, minDelayMs: 40 });
    this.markEntityCuratorReviewCompleteFromModal?.();
    this.showEntityCuratorModal = false;
    return true;
  },

  async automationResolveCaseInconsistencies() {
    await this.openCaseInconsistencyReviewModal({ automated: true });
    const result = this.applyAllSafeCaseInconsistencyFixes?.();
    if (isPromiseLike(result)) await result;
    const analysis = this.refreshCaseInconsistencyReview?.({ silent: true });
    if ((analysis?.pendingCount || 0) === 0) {
      this.markCaseInconsistencyReviewComplete?.();
      this.showCaseInconsistencyModal = false;
    } else if (this.caseAutomation.skipResolvableReview) {
      this.caseSupportState.workflowState.manualCompleteByTag = {
        ...(this.caseSupportState.workflowState.manualCompleteByTag || {}),
        CASE_INCONSISTENCIES: {
          completedAtUtc: new Date().toISOString(),
          revision: Number(this.caseSupportState.caseDataRevision) || 0,
          source: 'case-automation-next-skip-review'
        }
      };
      this.persistCaseSupportState?.();
      this.showCaseInconsistencyModal = false;
    } else {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'case-inconsistencies',
        stepTag: 'CASE_INCONSISTENCIES',
        title: 'Review remaining inconsistencies',
        message: `${analysis?.pendingCount || 0} inconsistency card${(analysis?.pendingCount || 0) === 1 ? '' : 's'} still need judgement after safe fixes. Review them, then choose Done / Continue.`,
        primaryLabel: 'Open inconsistency review',
        primaryMethod: 'openCaseInconsistencyReviewModal'
      });
    }
    return true;
  },

  async automationNormalizeDepositHotWalletNotes() {
    await this.openDepositHotWalletModal({ automated: true });
    const analysis = this.refreshDepositHotWalletReview?.({ silent: true, resetSelection: true });
    if ((analysis?.eligibleLocalCount || 0) > 0) {
      this.setDepositHotWalletVisibleSelection?.(true);
      const result = this.applySelectedDepositHotWalletSafeNormalizations?.();
      if (isPromiseLike(result)) await result;
    }
    const after = this.refreshDepositHotWalletReview?.({ silent: true, resetSelection: true });
    if ((after?.eligibleLocalCount || 0) === 0) {
      this.markDepositHotWalletReviewComplete?.();
      this.showDepositHotWalletModal = false;
    }
    return true;
  },

  async automationCompleteDepositPatternReview() {
    await this.openDepositPatternReviewModal?.({ automated: true });
    this.dismissRemainingDepositPatternCandidatesAndComplete?.();
    this.showDepositPatternModal = false;
    return true;
  },

  async automationReviewKnownEntityCasing() {
    await this.openKnownEntityCasingReviewModal?.({ automated: true });
    const analysis = this.refreshKnownEntityCasingReview?.({ silent: true });
    const pending = (analysis?.candidates || []).filter(candidate => !candidate.hasCurrentDecision);
    const result = this.applyKnownEntityCasingFixes?.(pending, { skipConfirm: true, source: 'case-automation-next' });
    if (isPromiseLike(result)) await result;
    this.dismissRemainingKnownEntityCasingCandidatesAndComplete?.();
    this.showKnownEntityCasingModal = false;
    return true;
  },

  async automationLabelDuplicateHashes(plan = {}) {
    await this.openDuplicateHashLabelerModal?.({ automated: true });
    const result = this.applyAllEligibleDuplicateHashUpdates?.({ skipConfirm: true, source: 'case-automation-next' });
    if (isPromiseLike(result)) await result;
    const after = this.refreshDuplicateHashLabeler?.({ silent: true });
    if ((after?.blockedProposalCount || 0) > 0) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'duplicate-hash-safety-review',
        stepTag: plan.stepTag || 'DUPLICATE_HASH_LABELS',
        stepTitle: plan.stepTitle || 'Label duplicate transaction hashes',
        title: 'Review protected duplicate-hash notes',
        message: `${after.blockedProposalCount} duplicate-hash note proposal${after.blockedProposalCount === 1 ? '' : 's'} were blocked because they would change non-duplicate Column J notes. Review those rows manually, then choose Done / Continue.`,
        primaryLabel: 'Open duplicate-hash labeler',
        primaryMethod: 'openDuplicateHashLabelerModal'
      });
      return false;
    }
    if ((after?.eligibleEntryCount || 0) > 0) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'duplicate-hash-updates-remaining',
        stepTag: plan.stepTag || 'DUPLICATE_HASH_LABELS',
        stepTitle: plan.stepTitle || 'Label duplicate transaction hashes',
        title: 'Duplicate-hash updates still remain',
        message: `${after.eligibleEntryCount} duplicate-hash note update${after.eligibleEntryCount === 1 ? '' : 's'} still need attention. Review the labeler manually, then choose Done / Continue.`,
        primaryLabel: 'Open duplicate-hash labeler',
        primaryMethod: 'openDuplicateHashLabelerModal'
      });
      return false;
    }
    this.markDuplicateHashLabelerReviewComplete?.(after, { source: 'case-automation-next' });
    this.showDuplicateHashLabelerModal = false;
    return true;
  },

  async automationBackupAndRewriteNotes() {
    await this.openNoteHashRewriteModal?.({ automated: true });
    this.createOrMergeOriginalNotesBackup?.({ replace: false });
    const result = this.applyAllEligibleNoteHashRewrites?.({ skipConfirm: true, source: 'case-automation-next' });
    if (isPromiseLike(result)) await result;
    this.markNoteHashRewriteReviewComplete?.(null, { source: 'case-automation-next' });
    this.showNoteHashRewriteModal = false;
    return true;
  },

  async automationReviewWwwUsdReferences() {
    await this.openWwwUsdReferenceReviewModal?.({ automated: true });
    await this.estimateWhatWentWhereUsdReferences?.({ selectedOnly: false, silent: true });
    this.toggleAllVisibleWwwUsdReferences?.(true);
    await resolveMaybePromise(this.applySelectedWhatWentWhereUsdReferences?.({ silent: true, confirm: false }));
    await resolveMaybePromise(this.stageWwwUsdReferenceReviewAudit?.({ silent: true }));
    await resolveMaybePromise(this.markWwwUsdReferenceReviewComplete?.({ source: 'case-automation-next' }));
    this.showWwwUsdReferenceModal = false;
    return true;
  },

  async automationBuildAndReviewAddressLegend() {
    await this.openAddressLegendModal?.({ automated: true });
    await this.buildCurrentAddressLegend?.({ silent: true });
    await this.markAddressLegendReviewComplete?.({ source: 'case-automation-next' });
    this.showAddressLegendModal = false;
    return true;
  },

  async automationBuildAndReviewTemplate() {
    await this.openTemplateDeliverableModal?.({ automated: true });
    await resolveMaybePromise(this.stageTemplateDeliverablePreview?.({ silent: true }));
    await resolveMaybePromise(this.markTemplateDeliverableReviewComplete?.({ source: 'case-automation-next' }));
    this.showTemplateDeliverableModal = false;
    return true;
  },

  async automationBuildAndReviewSuspiciousTable() {
    await this.openSuspiciousAddressTableModal?.({ automated: true });
    await resolveMaybePromise(this.stageSuspiciousAddressTablePreview?.({ silent: true }));
    await resolveMaybePromise(this.markSuspiciousAddressTableReviewComplete?.({ source: 'case-automation-next' }));
    this.showSuspiciousAddressTableModal = false;
    return true;
  },

  async automationBuildAndReviewExchangeTable() {
    await this.openExchangeAddressTableModal?.({ automated: true });
    await resolveMaybePromise(this.stageExchangeAddressTablePreview?.({ silent: true }));
    await resolveMaybePromise(this.markExchangeAddressTableReviewComplete?.({ source: 'case-automation-next' }));
    this.showExchangeAddressTableModal = false;
    return true;
  },

  async automationBuildAndReviewDormantDeliverables() {
    await this.openDormantAddressDeliverablesModal?.({ automated: true });
    await resolveMaybePromise(this.stageDormantAddressTablePreview?.({ silent: true }));
    await resolveMaybePromise(this.stageDormantAddressMonitoringPreview?.({ silent: true }));
    await resolveMaybePromise(this.markDormantAddressDeliverablesReviewComplete?.({ source: 'case-automation-next' }));
    this.showDormantAddressDeliverablesModal = false;
    return true;
  },

  async automationBuildAndReviewWhatWentWhere() {
    await this.openWhatWentWhereModal?.({ automated: true });
    await resolveMaybePromise(this.stageWhatWentWherePreview?.({ silent: true }));
    await resolveMaybePromise(this.stageWhatWentWhereValuationAudit?.({ silent: true }));
    await resolveMaybePromise(this.markWhatWentWhereReviewComplete?.({ source: 'case-automation-next' }));
    this.showWhatWentWhereModal = false;
    return true;
  },

  async automationBuildAndReviewConclusion() {
    await this.openCaseConclusionModal?.({ automated: true });
    await resolveMaybePromise(this.stageCaseConclusionPreview?.({ silent: true }));
    await resolveMaybePromise(this.markCaseConclusionReviewComplete?.({ source: 'case-automation-next' }));
    this.showCaseConclusionModal = false;
    return true;
  },

  async automationBuildAndReviewReportBody() {
    await this.openReportBodyModal?.({ automated: true });
    await resolveMaybePromise(this.stageReportBodyPreview?.({ variantId: 'standard', silent: true }));
    await resolveMaybePromise(this.markReportBodyReviewComplete?.({ source: 'case-automation-next' }));
    this.showReportBodyModal = false;
    return true;
  },

  async automationReviewAddendumPathEndings() {
    await this.openAddendumPathEndingReviewModal?.({ automated: true });
    await resolveMaybePromise(this.stageAddendumPathEndingReviewRows?.({ silent: true }));
    await resolveMaybePromise(this.markAddendumPathEndingReviewComplete?.({ source: 'case-automation-next' }));
    this.showAddendumPathEndingReviewModal = false;
    return true;
  },

  async automationResolveAddendumRecipientsOrPause() {
    await this.openAddendumRecipientEntityModal?.({ automated: true });
    const state = this.refreshAddendumRecipientEntityReview?.({ silent: true });
    if ((state?.needsChoiceCount || 0) > 0 || (state?.noCandidateEntityCount || 0) > 0) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'addendum-recipients',
        stepTag: 'ADDENDUM_RECIPIENT_ENTITY',
        title: 'Resolve addendum recipient entities',
        message: `${state.needsChoiceCount || 0} row${(state.needsChoiceCount || 0) === 1 ? '' : 's'} need an entity choice and ${state.noCandidateEntityCount || 0} row${(state.noCandidateEntityCount || 0) === 1 ? '' : 's'} have no candidate entity. Resolve them, then choose Done / Continue. Enable the skip toggle to pick the first available entity where possible.`,
        primaryLabel: 'Open recipient resolver',
        primaryMethod: 'openAddendumRecipientEntityModal'
      });
      return true;
    }
    await resolveMaybePromise(this.stageAddendumRecipientEntityRows?.({ silent: true }));
    await resolveMaybePromise(this.markAddendumRecipientEntityReviewComplete?.({ source: 'case-automation-next' }));
    this.showAddendumRecipientEntityModal = false;
    return true;
  },

  async automationResolveAddendumRecipientsWithFirstChoice() {
    await this.openAddendumRecipientEntityModal?.({ automated: true });
    const state = this.refreshAddendumRecipientEntityReview?.({ silent: true });
    (state?.candidates || []).forEach((candidate) => {
      if (!candidate?.needsUserChoice) return;
      const firstEntity = Array.isArray(candidate.candidateEntityNames) ? candidate.candidateEntityNames.find(Boolean) : '';
      if (firstEntity) this.saveAddendumRecipientEntityChoice?.(candidate, firstEntity, { silent: true, source: 'case-automation-next-skip-review' });
    });
    await resolveMaybePromise(this.stageAddendumRecipientEntityRows?.({ silent: true }));
    await resolveMaybePromise(this.markAddendumRecipientEntityReviewComplete?.({ source: 'case-automation-next' }));
    this.showAddendumRecipientEntityModal = false;
    return true;
  },

  async automationBuildAndReviewAddendum() {
    await this.openCaseAddendumModal?.({ automated: true });
    const state = this.refreshCaseAddendumReview?.({ silent: true }) || this.getCaseAddendumState?.();
    if (!state?.canBuild) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'addendum-build',
        stepTag: 'ADDENDUM_BUILD',
        title: 'Build and review addendum',
        message: state?.summary || 'The addendum prerequisites are not current yet. Review the path-ending rows and recipient entity step, then continue.',
        primaryLabel: 'Open addendum builder',
        primaryMethod: 'openCaseAddendumModal'
      });
      return true;
    }
    if ((state.resolvedRowCount || 0) > 0) {
      await resolveMaybePromise(this.stageCaseAddendumPreview?.({ variantId: 'tool18e_structured', state, silent: true }));
    }
    const marked = await resolveMaybePromise(this.markCaseAddendumReviewComplete?.({ source: 'case-automation-next', state }));
    if (marked === false) {
      this.openCaseAutomationManualPause({
        type: 'manual-pause',
        pauseKind: 'addendum-build',
        stepTag: 'ADDENDUM_BUILD',
        title: 'Build and review addendum',
        message: this.caseAddendumStatus || 'Case Buddy could not mark the addendum reviewed automatically. Review the addendum panel, then continue.',
        primaryLabel: 'Open addendum builder',
        primaryMethod: 'openCaseAddendumModal'
      });
      return true;
    }
    this.showCaseAddendumModal = false;
    return true;
  },

  async automationReviewCaseFlowVisuals() {
    await this.openCaseFlowVisualsModal?.({ automated: true });
    await resolveMaybePromise(this.markCaseFlowVisualsDone?.({ source: 'case-automation-next' }));
    this.showCaseFlowVisualsModal = false;
    return true;
  },

  async automationReviewFullReportPackage() {
    await this.openCaseFullReportPackageModal?.({ automated: true });
    await resolveMaybePromise(this.stageCaseFullReportPackagePreview?.({ silent: true }));
    await resolveMaybePromise(this.markCaseFullReportPackageReviewComplete?.({ source: 'case-automation-next' }));
    this.showCaseFullReportPackageModal = false;
    return true;
  }
};
